import React, { useState, useEffect, ReactNode } from 'react';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Stack } from 'components/Stack';
import styled from 'styled-components';
import { StackProps } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';
import { useForm } from 'react-hook-form';

import MessageLayout from '../common/MessageLayout';
import { ReactComponent as IconBoxFlat } from 'constants/icon/icon_box_flat.svg';
import { ReactComponent as IcPlusBlue } from 'constants/icon/ic_plus_blue.svg';
import theme from 'constants/theme';
import MessageCover from '../common/MessageCover';
import AutoMessageComponent from './AutoMessageComponent';
import { AlertModal } from 'components/Modal/AlertModal';
import StatusTag from 'pages/Setting/common/StatusTag';
import { IAutoMessage } from './AutoTablePart';
import { useSingleAutoMessage } from './AutoStore';
import { useSelectingRow } from '../common/CheckSelectStore';
import useToastStore from 'components/Toast/ToastStore';
import { useIsDifferenceStore } from '../common/MessagePromptStore';
import { patchMessageAuto, IRequestPatchMessageAutoParams } from '../api';
import { TRequestFilter } from '.';

export type TLocalForm = {
  isChecked: boolean;
  sendStartHour: number;
  sendStartMinute: number;
  sendEndHour: number;
  sendEndMinute: number;
};

type Props = {
  requestFilter: TRequestFilter;
};
const AutoMessage = ({ requestFilter }: Props) => {
  const { addToast } = useToastStore();
  const { singleMessage, setSingleMessage } = useSingleAutoMessage();
  const { selectRow, setselectRow } = useSelectingRow();
  const { isDifference, setIsDifference } = useIsDifferenceStore();
  const [isOpenCancel, setIsOpenCancel] = useState(false);
  const [isOpenSaveError, setIsOpenSaveError] = useState(false);

  const { register, handleSubmit, watch, setValue } = useForm<TLocalForm>({
    defaultValues: {
      isChecked: false,
      sendStartHour: 0,
      sendStartMinute: 0,
      sendEndHour: 0,
      sendEndMinute: 0,
    },
  });

  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (params: IRequestPatchMessageAutoParams) => {
      // console.log('onmutationFn');
      return patchMessageAuto(params);
    },
    onMutate: async () => {
      // console.log('onMutate');
    },
    onSuccess: async () => {
      // 성공 시 -  토스트 메시지+ select 취소처리 + 메시지 빈화면 처리 + (왼쪽)메시지 리스트 다시 불러오기
      await queryClient.invalidateQueries(['autoMessageList', requestFilter]);
      addToast('새 자동 발송 메시지가 저장되었습니다.', 3000);
      setSingleMessage(null);
      setselectRow('');
      setIsDifference(false);
    },
    onError: async () => {
      // 실패 시 -  에러 모달 띄우기
      setIsOpenSaveError(true);
    },
  });

  const onClickCancel = () => {
    setIsOpenCancel(true);
  };

  const onClickSave = async () => {
    if (singleMessage) {
      const timeFormat = (time: number) => (time < 10 ? `0${time}` : `${time}`);
      const startTime = `${timeFormat(watch().sendStartHour)}:${timeFormat(watch().sendStartMinute)}`;
      const endTime = `${timeFormat(watch().sendEndHour)}:${timeFormat(watch().sendEndMinute)}`;
      mutate({
        id: singleMessage?.code,
        isException: watch('isChecked'),
        isUse: singleMessage?.status,
        exceptionTime: `${dayjs(startTime, 'HH:mm').format('HH:mm:ss')}-${dayjs(endTime, 'HH:mm').format('HH:mm:ss')}`,
      });
    }
  };

  const onClickModalCancel = () => {
    setIsOpenCancel(false);
    setSingleMessage(null);
    setselectRow('');
    setIsDifference(false);
  };
  const onClickModalContinue = () => {
    setIsOpenCancel(false);
  };

  // 취소 시
  // 1. 다른 row를 클릭하여 진행했을 경우에는 [경고 modal 이후 row 해제 + 선택한 다른 row로 변경] 선택됨 - 테이블 row 선택시 해당모달 넣기
  // 2. 해당 페이지가 아닌 다른 탭으로 이탈을 시도할 경우에는 [경고 modal 이후 다른 탭으로 즉시 이동]됨 -
  // 3,4 번은 같은 처리
  // 3. '수신인 종류' 등 필터 항목을 변경하였을 때, 필터 항목에 해당 row가 해당하지 않는다면 말씀하신 것처럼 [경고 modal 이후 row 해제 + 빈창으로 변경]
  // 4. 우측 하단의 '취소' 버튼을 클릭하였을 때에도 마찬가지로 [경고 modal 이후 row 해제 + 빈창으로 변경]

  const renderStatusTag = (params: IAutoMessage) => {
    const mapper: { [key in string]: ReactNode } = {
      // 시작 | 중간 | 완료 | 1차보고 | 최종보고
      1: <StatusTag styleName="caption1" text={'시작'} color="RC02" borderColor="RC02" />,
      2: <StatusTag styleName="caption1" text={'중간'} color="RG10" borderColor="RG10" />,
      3: <StatusTag styleName="caption1" text={'완료'} color="RC11" borderColor="RC11" />,
      4: <StatusTag styleName="caption1" text={'1차보고'} color="RG04" borderColor="RG04" />,
      5: <StatusTag styleName="caption1" text={'최종보고'} color="RC04" borderColor="RC04" />,
    };
    return mapper[params.process] ?? '';
  };

  return (
    <>
      <MessageLayout
        titleComponent={
          <TitleDiv>
            <IconBoxFlat width={20} height={20} />
            {singleMessage && renderStatusTag(singleMessage)}
            <Text
              styleName="headline1"
              color="RG02"
              sx={{
                fontSize: 18,
                fontFamily: 'Pretendard',
                fontWeight: 700,
              }}
            >
              {singleMessage ? `${singleMessage?.title}` : '발송 내용'}
            </Text>
          </TitleDiv>
        }
        messageComponent={
          !!singleMessage ? (
            <AutoMessageComponent watch={watch} setValue={setValue} />
          ) : (
            <MessageCover firstLineMessage="상세 내용을 확인할" secondLineMessage="알림메시지를 선택해주세요." />
          )
        }
        buttonComponent={
          singleMessage && (
            <>
              <ButtonDiv>
                <CancelButtonStyle type="button" variant="secondary" h={32} onClick={onClickCancel}>
                  <ButtonText
                    disable={false}
                    style={{
                      color: theme.colors.RG03,
                      textAlign: 'center',
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    취소하기
                  </ButtonText>
                </CancelButtonStyle>

                <SaveButtonStyle type="button" variant="secondary" h={32} onClick={onClickSave}>
                  <ButtonText disable={false}>저장하기</ButtonText>
                </SaveButtonStyle>
              </ButtonDiv>
            </>
          )
        }
      ></MessageLayout>
      <AlertModal
        type="WARNING"
        isOpen={isOpenCancel}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              작성을 취소할까요?
            </Text>
            <Text styleName="body2" color="RG02">
              작성 중이던 내용은 저장되지 않습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <ModalButtonDiv>
            <Button variant="tertiary" h={40} sx={{ width: '100%', marginTop: '40px' }} onClick={onClickModalContinue}>
              <Text styleName="body1" color="RG03">
                계속 작성
              </Text>
            </Button>
            <Button variant="primary" h={40} sx={{ width: '100%', marginTop: '40px' }} onClick={onClickModalCancel}>
              <Text styleName="body1" color="RG00">
                작성 취소
              </Text>
            </Button>
          </ModalButtonDiv>
        }
      />
      <AlertModal
        type="WARNING"
        isOpen={isOpenSaveError}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              저장 요청에 실패했습니다.
            </Text>
            <Text styleName="body2" color="RG02">
              확인 후 제요청해 주세요.
            </Text>
          </Stack>
        }
        buttonGroup={
          <Button
            variant="tertiary"
            h={40}
            sx={{ width: '100%', marginTop: '40px' }}
            onClick={() => {
              setIsOpenSaveError(false);
            }}
          >
            <Text styleName="body1" color="RG03">
              확인
            </Text>
          </Button>
        }
      />
    </>
  );
};

export default AutoMessage;

const TitleDiv = styled.div<StackProps>`
  display: flex;
  padding: 20px;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  ${({ theme }) => ({
    borderBottom: `1px solid ${theme.colors.RG06}`,
  })}
`;

const ButtonDiv = styled.div<StackProps>`
  display: flex;
  padding: 20px;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  align-self: stretch;

  ${({ theme }) => ({
    borderRadius: `0px 0px 8px 8px`,
    borderTop: `1px solid ${theme.colors.RG06}`,
    background: '#fff',
  })}
`;

const SaveButtonStyle = styled(Button)`
  display: flex;
  width: 216px;
  height: 40px;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  ${({ theme, disabled }) => ({
    borderRadius: 8,
    backgroundColor: `${disabled ? theme.colors.RG07 : '#2E66F6'}`,
    borderColor: `${disabled ? theme.colors.RG07 : theme.colors.RG00}`,
  })}
`;
type ButtonTextProps = {
  disable: boolean;
};
const ButtonText = styled.text<ButtonTextProps>`
  ${({ theme, disable }) => ({
    color: `${disable ? theme.colors.RG04 : theme.colors.RG00}`,
  })}
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const CancelButtonStyle = styled(Button)`
  display: flex;
  width: 102px;
  height: 40px;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  ${({ theme }) => ({
    borderRadius: 8,
    border: `1px solid ${theme.colors.RG06}`,
    backgroundColor: theme.colors.RG00,
  })}
`;

const ModalButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`;
